import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, AlertCircle } from 'lucide-react';

// Set up PDF.js worker — use jsdelivr CDN which syncs npm packages faster than cdnjs
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFViewer = ({
  pdfFile,
  pdfUrl,
  currentPage,
  onDocumentLoad,
  onPageLoad,
  onPageClick,
  onDragOver,
  onDrop,
  children,
  className = '',
}) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);

  // Calculate page width to fit container
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Leave some padding
        const containerWidth = containerRef.current.offsetWidth - 32;
        setPageWidth(Math.min(containerWidth, 800)); // Max 800px
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleDocumentLoadSuccess = useCallback(({ numPages }) => {
    setLoading(false);
    setError(null);
    if (onDocumentLoad) {
      onDocumentLoad({ numPages });
    }
  }, [onDocumentLoad]);

  const handleDocumentLoadError = useCallback((err) => {
    setLoading(false);
    setError('Failed to load PDF document');
    console.error('PDF load error:', err);
  }, []);

  const handlePageLoadSuccess = useCallback((page) => {
    if (onPageLoad) {
      // Get the rendered page dimensions
      const { width, height } = page;
      onPageLoad({
        width,
        height,
        pageNumber: currentPage,
      });
    }
  }, [onPageLoad, currentPage]);

  const handleContainerClick = useCallback((e) => {
    // Only handle clicks on the PDF container itself, not on field overlays
    if (e.target === e.currentTarget || e.target.closest('.pdf-page-container')) {
      if (onPageClick) {
        const rect = e.currentTarget.querySelector('.pdf-page-container')?.getBoundingClientRect();
        if (rect) {
          onPageClick(e, rect);
        }
      }
    }
  }, [onPageClick]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (onDragOver) {
      onDragOver(e);
    }
  }, [onDragOver]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (onDrop) {
      const pageContainer = e.currentTarget.querySelector('.pdf-page-container');
      const rect = pageContainer?.getBoundingClientRect();
      if (rect) {
        onDrop(e, rect);
      }
    }
  }, [onDrop]);

  // Determine the file source
  const fileSource = pdfFile || pdfUrl;

  if (!fileSource) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 min-h-[400px] ${className}`}
      >
        <div className="text-center text-gray-500">
          <p className="text-lg font-medium">No PDF loaded</p>
          <p className="text-sm mt-1">Upload a PDF to start adding fields</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative bg-gray-200 rounded-lg overflow-auto ${className}`}
      onClick={handleContainerClick}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading PDF...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-6 h-6" />
            <span>{error}</span>
          </div>
        </div>
      )}

      <Document
        file={fileSource}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={handleDocumentLoadError}
        loading={null}
        className="flex justify-center p-4"
      >
        <div className="pdf-page-container relative inline-block shadow-lg">
          <Page
            pageNumber={currentPage}
            width={pageWidth}
            onLoadSuccess={handlePageLoadSuccess}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={null}
          />
          {/* Field overlays rendered as children */}
          {children}
        </div>
      </Document>
    </div>
  );
};

export default PDFViewer;
